import { PromoCodeResult } from 'api';
import { RegionPriceType } from 'api/useGetRegionPrice';
import { ReductionType } from 'AppEnums';
import { Panel } from 'config/types';
import { MapDetailState } from 'schemas/mapDetailSchema';
import { getPanelOptions } from '../getPanelOptions';

export const GetTotalC02Saved = (kwh: number) => kwh * 0.17; // 0,17 KG par kwh produit

export const GetTotalTreeSaved = (kwh: number): number =>
  Math.ceil(GetTotalC02Saved(kwh) / 21); // 21kg/year per tree

export const GetPanelSizeOnMapWithRoofAngle = (
  roofAngle: number,
  panelHeight: number
) => panelHeight * Math.cos((roofAngle * Math.PI) / 180);

export const GetPeakPower = (numPanels: number, panelPower: number) =>
  numPanels * panelPower;

// TODO: unit test this
export const getPricePerQuantity = (
  priceList: { quantity: { from: number }; price: number }[],
  quantity: number
) => {
  const sortedPriceList = priceList.sort(
    (a, b) => b.quantity.from - a.quantity.from
  );
  // console.log('priceList : ', priceList);
  // console.log('sortedPriceList : ', sortedPriceList);
  return sortedPriceList.find((x) => x.quantity.from <= quantity)?.price || 0;
};

/**
 * Get the power of a panel based on its id
 * NOTE: it's in kW must be x1000 to get value from API items
 */
export const getPanelPower = (panelId: string, panelList: Panel[]) =>
  getPanelOptions(panelId, panelList)!.power;

export const getPricePerPanel = (
  panelId: string,
  numPanels: number,
  panelList: Panel[]
) => {
  const panelPriceList = getPanelOptions(panelId, panelList)?.prices;
  if (panelPriceList) {
    const price = getPricePerQuantity(panelPriceList, numPanels);
    if (price) return price;
  }
  throw new Error(`panel price not found for :${panelId}`);
};

export const getEstimatedPanelsForConsumption = (
  panelId: string,
  panelList: Panel[],
  consumption: number
) => {
  const panelPower = getPanelPower(panelId, panelList);
  return Math.ceil(consumption / (1000 * panelPower));
};

/**
 * Retrieve house orientation based on all panels.
 *  SOUTH is 0 - NORTH is 180
 * TODO: To be exact, we shouls call the PVCalc api with each panel lines.
 * here we do an "average" of orientation based on lines and num panels.
 * @param mapDetail
 * @returns angle:number
 */
export const GetHouseOrientation = (
  mapDetail: MapDetailState | undefined
): number => {
  let totalPanels = 0;
  let totalAngle = 0;
  if (mapDetail) {
    // we are using absolute angles for the average angle calculation in calse of multiple panel lines
    const useAbsoluteAngle = mapDetail.panels.length > 1;

    mapDetail.panels.forEach((panelLine) => {
      // console.log(`angle is :${panelLine.angle}`);
      totalPanels += panelLine.panelAmount!;
      const angle = useAbsoluteAngle
        ? Math.abs(panelLine.angle!)
        : panelLine.angle!;
      totalAngle += panelLine.panelAmount! * angle;
    });
    return totalAngle / totalPanels;
  }

  // if no map detail, we are South orientated
  return 0;
};

/**
 * Based on estimated yearly production of the panels,
 * and the auto consumption rate and the region prices and primes,
 * we can estimate the yearly gain of the panels.
 * @param annualProduction
 * @param hasBattery
 * @param regionOptions
 */
export const getCalculatedYearlyGain = (
  currentAnnualProduction: number,
  estimatedPanelsProduction: number,
  peakPower: number,
  hasBattery: Boolean,
  regionOptions: RegionPriceType
) => {
  const autoConsumptionRate = hasBattery ? 0.64 : 0.34;
  let autoConsumptionValue = estimatedPanelsProduction * autoConsumptionRate;
  if (autoConsumptionValue > 0.75 * currentAnnualProduction)
    autoConsumptionValue = currentAnnualProduction * 0.75;

  const injectionValue = estimatedPanelsProduction - autoConsumptionValue;

  const pricePerKWh = regionOptions.price_cons;
  const priceInjectionPerKwh = regionOptions.price_inject;

  const autoConsumptionValueInEuro = pricePerKWh * autoConsumptionValue;
  const injectionValueInEuro = priceInjectionPerKwh * injectionValue;
  const gain = autoConsumptionValueInEuro + injectionValueInEuro;

  const kva = peakPower / 1.4;
  const providerGrd = (regionOptions.provider_grd || 0) * kva;
  const regionHelp = regionOptions.subside * estimatedPanelsProduction;

  const netGain = gain + regionHelp - providerGrd;
  return netGain;
};

export const getPromoPrice = (
  price: number,
  promoCodeDetails: PromoCodeResult,
  peakPower: number
) => {
  if (promoCodeDetails) {
    if (promoCodeDetails.reductionType === ReductionType.Percent)
      return (price * (100 - promoCodeDetails.reductionPercent)) / 100;
    return price - promoCodeDetails.valuePerKwh * peakPower * 1000;
  }
  return price;
};

export const calculateConsumptionBaseOnBill = (
  billAmount: number,
  regionPrice: RegionPriceType | undefined | null
) => (regionPrice ? (billAmount * 12) / regionPrice.price_cons : 0);

/*

J'ai mis dans l'excel la production théorique par panneau justement dans ce but.

Ensuite si la personne indique en euros par mois, il suffit de multiplier par 12 pour avoir le budget annuel et de diviser par 0,37 qui est le prix de l'électricité. (Paramètre) pour avoir la consommation.

Donc si 100 euros par mois => 1200 euros par an => 3243 kWh.

Si on se base sur la production moyenne par panneau et qu'on arrondi ça donne 3243/370 = 9 panneaux.
*/
